import { EmptyMessageCustomization } from "@multiweb/frames";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../UI.BaseControls/Control";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { EmptyDatagridMessageType, IEmptyDataGridMessage } from "./IEmptyDataGridMessage";

/**
* Hláška, která se zobrazuje v situaci, kdy Datagrid nemá žádné záznamy, které by zobrazil
*/
export class EmptyDataGridMessage extends Control implements IEmptyDataGridMessage {
    /** element hlasky */
    /** element s ikonou */
    protected _iconElement: HTMLElement;
    /** element s popiskem */
    protected _descElement: HTMLElement;

    private _customizeProps: EmptyMessageCustomization = {};

    public set messageType(v: EmptyDatagridMessageType) {
        this.setAttribute("messagetype", v.toString());
    }
    public get messageType(): EmptyDatagridMessageType {
        return parseInt(this.getAttribute("messagetype"));
    }

    public get emptyMessageCustomization(): EmptyMessageCustomization {
        return { ...this._customizeProps };
    }
    public set emptyMessageCustomization(value: EmptyMessageCustomization) {
        this._customizeProps = { ...value };
        this._resetIconState();
        this._setMessageType(this.messageType);
    }

    protected static override get observedAttributes(): string[] {
        return ["messagetype"].concat(Control.observedAttributes);
    }

    public fulltextSearchString: string;

    protected override _constructor(): void {
        super._constructor();

        this._iconElement = document.createElement("div");
        this._descElement = document.createElement("div");

        this.classList.add("EmptyMessage");
        this._iconElement.classList.add("Icon");
        this._descElement.classList.add("Message");

        this.appendChild(this._iconElement);
        this.appendChild(this._descElement);
        this._descElement.textContent = "";
        this._setMessageType(EmptyDatagridMessageType.dataLoading);
        this.visible = false;
    }

    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        switch (attrName) {
            case "messagetype":
                this._setMessageType(parseInt(newVal));
                break;
        }
    }

    /**
     * Nastaví element do režimu QueryMode, kdy se zobrazuje zpráva a tlačítko, umožňující načtení všech dat
     * @param dataLoad Funkce, spuštěná po kliku na tlačítko
     */
    public setTypeToQueryMode(dataLoad: Function): void {
        this.messageType = EmptyDatagridMessageType.queryModeEmpty;

        Localization.remLocData(this._descElement);

        const textElement = createElement({ type: "div" });
        Localization.addLocData(textElement, "GUI-676718"); // Pro načtení dat nastavte filtr
        this._descElement.appendChild(textElement);

        const loadButton = createElement({ type: "button", classes: "btn flat primary" });
        loadButton.addEventListener("click", () => {
            if (loadButton.classList.contains("Disabled"))
                return;
            dataLoad();
        });
        Localization.addLocData(loadButton, "GUI-676719"); // Pro načtení všech dat klikněte
        this._descElement.appendChild(loadButton);
    }

    /**
     * Nastaví odpovídající hlášku u prázdného DataGridu
     * @param messageType Typ hlášky
     */
    protected async _setMessageType(messageType: EmptyDatagridMessageType): Promise<void> {
        this._resetIconState();

        const {
            noItemsIcon,
            noItemsLocKey,
            noItemsFilterIcon,
            noItemsFilterLocKey,
            noItemsFulltextSearchIcon,
            noItemsFulltextSearchLocKey,
        } = this.emptyMessageCustomization;

        switch (messageType) {
            case EmptyDatagridMessageType.initialEmpty:
                this._iconElement.classList.add(noItemsIcon || "InitialEmpty");
                Localization.addLocData(this._descElement, noItemsLocKey || "GUI-489002"); //Žádné záznamy. V tomto seznamu nemáme co zobrazit.
                break;
            case EmptyDatagridMessageType.filterEmpty:
                this._iconElement.classList.add(noItemsFilterIcon || "FilterEmpty");
                Localization.addLocData(this._descElement, noItemsFilterLocKey || "GUI-488998"); //Rádi bychom něco zobrazili, ale zadaný filtr nám to nedovoluje.
                break;
            case EmptyDatagridMessageType.fulltextEmpty:
                this._iconElement.classList.add(noItemsFulltextSearchIcon || "FulltextEmpty");
                Localization.addLocData(this._descElement, noItemsFulltextSearchLocKey || "GUI-702817", undefined, undefined, [this.fulltextSearchString || ""]); //Snažili jsme se usilovně, ale '@TEXT' jsme nenašli.
                break;
            case EmptyDatagridMessageType.queryModeEmpty:
                this._iconElement.classList.add("InitialEmpty");
                break;
            case EmptyDatagridMessageType.dataLoading:
                this._iconElement.classList.add("data-loading");
                Localization.addLocData(this._descElement, "GUI-665555"); //Načítáme data
                break;
        }
    }

    /** Odstraní všechny css třídy z elementu ikony */
    protected _resetIconState(): void {
        const {
            noItemsIcon,
            noItemsFilterIcon,
            noItemsFulltextSearchIcon,
        } = this.emptyMessageCustomization;
        const classesToRemove = [noItemsIcon, noItemsFilterIcon, noItemsFulltextSearchIcon, "InitialEmpty", "FilterEmpty", "FulltextEmpty", "data-loading"].filter(className => className);
        this._iconElement.classList.remove(...classesToRemove);
    }
}

registerCustomElement("tsc-EmptyDataGridMessage", EmptyDataGridMessage);
